import { defaults, each, merge } from "lodash";

import strings from "@/utils/strings";
import store from "@/store";
import domo from "@/utils/domo";

import { communitySearch, communitySelect, systemSelect } from "@/views/system-select/paths";
import InstallNestedFlow from "@/views/install/nested-flow";

import { getCommunity } from "./helpers/community";

/**
 * Define application routes here.
 * The `view` attribute is relative to `@/views`.
 *
 * Supported 'meta' values:
 * - theme         : Vuetify theme (Default is 'light')
 * - layout        : Use a custom layout or set to 'false' for no layout.
 *                   Layouts live in `@/components/layout`.
 *                   (Default is 'layout-main')
 * - hideMenu      : Boolean specifying whether or not to force the side-bar
 *                   menu to stay closed. Defaults to false.
 * - {function(route) -> bool|object} [auth]         - Add authorization check
 *       to a route. Can either be a function or an object describing the CASL
 *       `action` and `subject` arguments required to check user abilities.
 * - {function(route) -> bool|string} auth.action    - Required CASL 'action'
 * - {function(route) -> bool|string} [auth.subject] - Optional CASL 'subject'
 *
 */
const paths = [
	{
		path: "",
		name: "Home",
		component: () => import("@/views/Home"),
		meta: {
			layout: false,
		},
	},
	{
		path: "/select",
		component: () => import("@/views/Home"),
		children: [
			merge({}, communitySelect, {
				name: "SelectContext",
			}),
			{
				...systemSelect,
			},
			{
				path: ":communityId/:systemId",
				redirect: { name: "Home" },
			},
		],
	},
	{
		path: "/login",
		name: "Login",
		meta: {
			theme: "dark",
			layout: false,
		},
		component: () => import("@/views/Login"),
	},
	{
		path: "/logout",
		name: "Logout",
		beforeEnter: async (to, from, next) => {
			try {
				await store.dispatch("auth/signOut");
				await store.dispatch("resetState");

				// Force reauthentication with Domo on next visit to dashboard page
				domo.clearSession();
			} catch (e) {
				return next(e);
			}

			// Navigate to login page
			next({ name: "Login" });
		},
	},
	{
		path: "/install",
		component: () => import("@/views/Install"),
		meta: {
			// Don't use a template, so that we can incorporate it directly in
			// `Install.vue` and override some of the named slots.
			layout: false,

			// Keep side-bar menu hidden while we're in an installation
			// session.
			hideMenu: true,

			auth: {
				action: "install",
				subject: "System",
			},
		},
		children: [
			merge({}, communitySelect, {
				name: "Install",
				components: {
					default: () => import("@/views/system-select/CommunitySelect"),
					header: () => import("@/views/install/community/Header"),
				},
				meta: {
					back: { name: "Home" },
				},
			}),
			merge({}, systemSelect, {
				components: {
					default: () => import("@/views/system-select/SystemSelect"),
					header: () => import("@/views/install/system/Header"),
				},
				meta: {
					back: { path: "/install" },
				},
			}),
		],
	},
	{
		path: "/install/:communityId/:systemId",
		component: InstallNestedFlow,
		meta: {
			layout: false,
		},
	},
	{
		path: "/project",
		name: "PM Home",
		component: () => import("@/views/Project"),
		meta: {
			layout: false,
			title: strings.project.titles.default,
			back: { name: "Home" },

			auth: {
				action: "list",
				subject: "AccessGroups",
			},
		},
		children: [
			{
				path: "groups",
				name: "PM Access Groups",
				component: () => import("@/views/project/AccessGroups"),
				meta: {
					title: strings.project.titles.accessGroups,
					back: { name: "PM Home" },
				},
			},
		],
	},
	{
		path: "/live",
		component: () => import("@/views/Live"),
		meta: {
			layout: false,

			auth: {
				action: "read",
				subject: "LiveView",
			},
		},
		children: [
			{
				path: "",
				redirect: { path: "community" },
			},
			merge({}, communitySearch, {
				path: "community",
				name: "Live Community Select",
				meta: { next: { name: "Live Community" } },
			}),
			{
				path: "community/:communityId",
				name: "Live Community",
				component: () => import("@/views/live/LiveCommunity"),

				meta: {
					auth: {
						action: "read",
						subject: getCommunity,
					},
				},
			},
			merge({}, systemSelect, {
				path: "system",
				name: "Live System Select",

				// Only show installed systems
				props: {
					installedOnly: true, // Only show installed systems
					confirmInstalled: false, // Don't show confirmation dialog
				},

				meta: { next: { name: "Live System" } },
			}),
			{
				path: "system/:systemId",
				name: "Live System",
				component: () => import("@/views/live/LiveSystem"),
			},
			{
				path: "system/:systemId/devices",
				name: "Live Devices",
				component: () => import("@/views/live/system/Devices"),
			},
			{
				path: "system/:systemId/scenes",
				name: "Live Scenes",
				component: () => import("@/views/live/system/Scenes"),
			},
			{
				path: "user/:userId",
				name: "Live User",
				component: () => import("@/views/live/LiveUser"),
			},
			{
				path: "user/:userId/edit",
				name: "Live User Edit",
				component: () => import("@/views/live/LiveUserEdit"),
			},
		],
	},
	// {
	// 	path: "/dashboard",
	// 	component: () => import("@/views/Dashboard"),
	// 	meta: {
	// 		layout: false,
	// 		auth: {
	// 			action: "read",
	// 			subject: "Report",
	// 		},
	// 	},
	// 	children: [
	// 		merge({}, communitySelect, {
	// 			name: "Dashboard",
	// 			meta: {
	// 				back: { name: "Home" },
	// 			},
	// 		}),
	// 		{
	// 			path: ":communityId",
	// 			props: true,
	// 			component: () => import("@/views/dashboard/CommunityReport"),
	// 			meta: {
	// 				back: { name: "Dashboard" },

	// 				auth: {
	// 					action: "read",
	// 					subject: getCommunity,
	// 				},
	// 			},
	// 		},
	// 	],
	// },
	{
		path: "/tickets",
		name: "K4NOC Tickets",
		component: () => import("@/views/Tickets"),
		children: [
			{
				path: "room-moves",
				name: "K4NOC Room Moves",
				component: () => import("@/views/tickets/RoomMoves"),
				meta: {
					back: { name: "Home" },
					title: strings.k4noc.titles.default,
					auth: {
						action: "read",
						subject: "K4NOC",
					},
				},
			},
			{
				path: "room-moves/:ticketId",
				props: true,
				component: () => import("@/views/tickets/RoomMoves/Ticket"),
				meta: {
					back: { name: "K4NOC Room Moves" },
				},
			},
			{
				path: "device-status",
				name: "K4NOC Device Status",
				component: () => import("@/views/tickets/DeviceStatus"),
				meta: {
					back: { name: "Home" },
					title: strings.k4noc.titles.default,
				},
			},
			{
				path: "device-status/:sysId",
				name: "K4NOC Device Status Ticket",
				props: true,
				component: () => import("@/views/tickets/DeviceStatus/Ticket"),
				meta: {
					back: { name: "K4NOC Device Status" },
				},
			},
		],
	},
	{
		path: "/lock-manager",
		component: () => import("@/views/Lock"),
		meta: {
			back: { name: "Home" },
			title: strings.menu.lock,
			layout: false,

			auth: {
				action: "read",
				subject: "LockManager",
			},
		},
		children: [
			{
				path: "",
				redirect: { path: "community" },
			},
			merge({}, communitySelect, {
				path: "community",
				name: "Lock Community Select",
				meta: { next: { name: "Lock Manager" } },
			}),
			{
				path: ":communityId",
				name: "Lock Manager",
				component: () => import("@/views/lock/Index"),
				meta: { layout: false },
			},
		],
	},
	{
		path: "/test",
		name: "Test",
		component: () => import("@/views/Test"),
	},
	{
		path: "/about",
		name: "About",
		component: () => import("@/views/About"),
	},
	{
		path: "/forbidden",
		name: "Forbidden",
		component: () => import("@/views/Forbidden"),
	},
	{
		path: "*",
		redirect: "/",
	},
];

const defaultMeta = {
	theme: "light",
	layout: "layout-main",
	hideMenu: false,
};

// Set default meta values for each path.
each(paths, path => {
	path.meta = defaults({}, path.meta, defaultMeta);
});

export default paths;
